import { Path, PathValue, useController, UseFormReturn } from "react-hook-form";
import { useTranslation } from "@vixi/localization";
import { StyledTextLink } from "@/components/links/TextLink";
import { ComponentProps } from "react";
import z from "zod/v3";
import { Img } from "../base/img";
import { RadioGroup, RadioGroupItemWithLabel } from "../base/radio-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "../base/tooltip";

export const manageAccessValidationSchema = z.object({
  accessBy: z.enum(["all", "invited"]).nullable(),
});
export type ManageAccessFormValues = z.infer<typeof manageAccessValidationSchema>;

// Constants
const screenProps = {
  contain: true,
  className: "w-45 h-90 mx-auto",
};

const SeeHowCodeUsed = () => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-y-6">
      <div>
        <p className="fs-20">{t("Tooltip.EnterCodeAfterInstall")}</p>
      </div>

      <div>
        <p className="fs-20">{t("Tooltip.LearnAfterJoining")}</p>
      </div>

      <div>
        <Img {...screenProps} src="/images/screens/paste-code.png" />
      </div>

      <div>
        <Img {...screenProps} src="/images/screens/avatar-in-course.png" />
      </div>
    </div>
  );
};

interface ManageAccessFormProps<T extends ManageAccessFormValues>
  extends ComponentProps<typeof RadioGroup> {
  form: UseFormReturn<T>;
  courseCode?: string;
}

export const ManageAccessForm = <T extends ManageAccessFormValues>({
  form,
  courseCode,
  ...rootProps
}: ManageAccessFormProps<T>) => {
  const { t, tFile } = useTranslation();
  const { control, watch } = form;
  const {
    field: { ref, ...field },
  } = useController({
    control,
    defaultValue: "" as PathValue<T, Path<T>>,
    name: "accessBy" as Path<T>,
  });

  watch((value) => {
    console.log(value);
  });

  return (
    <RadioGroup className="flex h-full items-center justify-around" {...rootProps} {...field}>
      <div>
        <Img contain src="/images/all-users.png" className="mx-auto h-68.75 w-67.75" />
        <RadioGroupItemWithLabel value={"all"} label={t("CourseDetailsForm.AccessByAll")} />
      </div>

      <div className="h-full w-0.5 bg-gray-300" />

      <div>
        <Img contain src={tFile("/images", "invited-users.png")} className="mx-auto h-68.75 w-67.75" />
        <div className="flex items-center gap-2">
          <RadioGroupItemWithLabel value={"invited"} label={t("CourseDetailsForm.AccessByCode")} />

          <Tooltip>
            <TooltipTrigger>
              <StyledTextLink>{t("Action.SeeHow")}</StyledTextLink>
            </TooltipTrigger>
            <TooltipContent
              portal={document.getElementById("tooltip-backdrop")}
              variant="default"
              side="top"
              className="max-w-232.5"
            >
              <SeeHowCodeUsed />
            </TooltipContent>
          </Tooltip>
        </div>

        {courseCode && (
          <p className="text-text-secondary" color="textSecondary">
            {`${t("CourseDetailsForm.YourEnrollmentCode")} ${courseCode}`}
          </p>
        )}
      </div>
    </RadioGroup>
  );
};
